// Transforms company profile API response to form defaultValues
import type { ProfileSection, ProfileFieldWithValue, RepeaterInstance } from '@startus/lib';
import type { FieldStatus } from '../types/fields';
import type { SectionConfig } from '../types/sections';

// Computes profile completion % using only form-visible fields (the sections config).
// This is the single source of truth — used by both the dashboard and the detail page.
// Counts only fields defined in SectionConfig, excluding backend-only fields like profile_assets.
export function computeProfileProgress(
  sections: SectionConfig[],
  profileData: Record<string, unknown>
): number {
  const allFields = sections.flatMap((s) => s.fields);
  if (allFields.length === 0) return 0;
  const filled = allFields.filter((f) => {
    const val = profileData[f.name];
    if (val === undefined || val === null || val === '') return false;
    return Array.isArray(val) ? val.length > 0 : true;
  }).length;
  return Math.round((filled / allFields.length) * 100);
}

// Maps company profile response to form default values
// Handles multiselect (JSON arrays) and repeaters (instances → array of objects)
export function mapProfileToFormValues(sections: ProfileSection[]): Record<string, any> {
  const values: Record<string, any> = {};

  sections.forEach((section: ProfileSection) => {
    section.fields.forEach((field: ProfileFieldWithValue) => {
      const fieldId = field.fieldId;

      // Repeater fields — build array from instances
      if (field.fieldType === 'RepeaterField') {
        values[fieldId] = mapRepeaterInstances(field.instances);
        return;
      }

      // Never-filled fields still need an explicit baseline (not an absent key) — otherwise
      // react-hook-form has no top-level defaultValue to compare against, and a field that
      // mounts/unmounts while being edited (e.g. tile-grid column rebalancing) re-captures its
      // dirty baseline from whatever is currently in the DOM, silently discarding the edit.
      if (field.value === null) {
        values[fieldId] =
          field.fieldType === 'MultiselectField' || field.fieldType === 'MultiselectDropdownField'
            ? []
            : '';
        return;
      }

      // Parse JSON strings back to arrays for multiselect fields
      if (
        field.fieldType === 'MultiselectField' ||
        field.fieldType === 'MultiselectDropdownField'
      ) {
        try {
          const parsed = JSON.parse(field.value);
          // If AI saved a plain string instead of array, wrap it
          values[fieldId] = Array.isArray(parsed) ? parsed : [String(parsed)];
        } catch {
          // Not valid JSON — plain string saved by AI, wrap as single-item array
          values[fieldId] = field.value ? [field.value] : [];
        }
        return;
      }

      // Regular fields — use string value directly
      values[fieldId] = field.value;
    });
  });

  return values;
}

// Per-field edit info extracted from profile response
export interface FieldEditInfo {
  editedBy: string | null;
  updatedAt: string | null;
  valueId: string | null;
  status: FieldStatus;
}

// Map backend status string (UPPER_CASE) to frontend FieldStatus (snake_case)
const BACKEND_STATUS_MAP: Record<string, FieldStatus> = {
  INCOMPLETE: 'incomplete',
  IN_PROGRESS: 'in_progress',
  AI_REVIEW: 'ai_review',
  MARKED_FOR_REVIEW: 'marked_for_review',
  VALIDATED: 'validated',
};

function mapBackendStatus(status: string | null): FieldStatus {
  return (status && BACKEND_STATUS_MAP[status]) || 'incomplete';
}

// Extract edit metadata (editedBy UUID, updatedAt, valueId, status) per field from profile response
export function mapProfileEditMetadata(sections: ProfileSection[]): Record<string, FieldEditInfo> {
  const metadata: Record<string, FieldEditInfo> = {};

  sections.forEach((section: ProfileSection) => {
    section.fields.forEach((field: ProfileFieldWithValue) => {
      metadata[field.fieldId] = {
        editedBy: field.editedBy,
        updatedAt: field.updatedAt,
        valueId: field.valueId,
        status: mapBackendStatus(field.status),
      };
    });
  });

  return metadata;
}

// Convert repeater instances to form array: [{__instanceId, childFieldId: value, ...}, ...]
// Preserves instanceId so save handler can reuse existing instances instead of creating new ones
// Skips empty instances (created but no values saved)
function mapRepeaterInstances(instances?: RepeaterInstance[]): Record<string, string>[] {
  if (!instances || instances.length === 0) return [];

  return instances
    .filter((inst: RepeaterInstance) => inst.values.length > 0)
    .sort((a: RepeaterInstance, b: RepeaterInstance) => a.index - b.index)
    .map((inst: RepeaterInstance) => {
      const item: Record<string, string> = { __instanceId: inst.instanceId };
      inst.values.forEach((v: { fieldId: string; value: string }) => {
        item[v.fieldId] = v.value;
      });
      return item;
    });
}
